// Playbooks: the commercial team's one list of what clients push back on and what we say to it (pipeline K3; module 10
// U244 to U252, U256, U257; the drawing is screens/2026-10-04-playbooks-concepts/compact-2-split.html, with the account
// owner's view and the states). The list of objection types on the left, most raised first; the picked one's sheet on the
// right: our usual answer, what clients say with our reply, who raised it, its history. An approver (any one of the four,
// work.copilot_approver) approves, edits, rejects, moves and retires; anyone suggests (U250). Who may is the database's
// to say on every press; the page only offers what the reader's role makes sensible.
import { ActionInput, ActionSelect, ActionTextarea, ActionButton } from '../parts/index.js';
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Unauthenticated, type Store } from '../api.js';
import { useLiveRefresh } from '../live.js';
import { PlaybooksError, playbooksApi, type BookEntry, type BookObjection, type EntryAct, type PlaybookBook } from './api.js';
import {
  ALLOCATE_SAID, allocatedOf, allocatedWords, closest, counts, dayOf, heardWhere, inTab, itemMatches, itemSource, listedInUse, NAME_MAX, nameOf, newObjection, ordered, raisedWords,
  rowsOf, rulesByFamily, scoped, searchOf, sourceWords, stateWord, viewOf, waitingOf, waits, yoursOf, type Allocated, type Item, type Line, type Row, type Tab, type View,
} from './book.js';
import { binnedSaid, MatchLine, placedSaid, pressItem, type Press } from './AllocateCard.js';
import './playbooks.css';

const NARROW = 760;
const UNDO_MS = 8000;

interface Toast { text: string; undo?: () => Promise<void> }
/** What one section last said about a press of the reader's: a failure, or another approver's change. */
type Notes = Record<string, string>;

function useNarrow(): boolean {
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < NARROW);
  useEffect(() => {
    const on = () => setNarrow(window.innerWidth < NARROW);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return narrow;
}

export function PlaybooksPage({ store, open: go }: { store: Store; open: (path: string) => void }) {
  const tenant = store.tenantId;
  const [book, setBook] = useState<PlaybookBook | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setViewState] = useState<View | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const [notes, setNotes] = useState<Notes>({});
  const [suggesting, setSuggesting] = useState(false);
  const [readAt, setReadAt] = useState<Date | null>(null);
  const [left, setLeft] = useState(0);
  const narrow = useNarrow();
  const toastTimer = useRef<number | null>(null);
  /** On a phone the sheet is its own page: opening one is a step in the browser's history, so Back returns to the list. */
  const pushed = useRef(false);
  const listY = useRef<number | null>(null);

  // Read again without clearing the page, so a press does not blank the list while it reloads.
  const load = useCallback(async () => {
    try {
      const b = await playbooksApi.book(tenant);
      setBook(b);
      setError(null);
      setReadAt(new Date());
      setViewState((v) => v ?? viewOf(window.location.search, b.canApprove));
    } catch (e: unknown) {
      if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [tenant]);
  useEffect(() => { void load(); }, [load]);
  useLiveRefresh(load);
  useEffect(() => () => { if (toastTimer.current) window.clearTimeout(toastTimer.current); }, []);

  const setView = useCallback((next: Partial<View>) => {
    if (!view || !book) return;
    const n = { ...view, ...next };
    const opening = narrow && 'open' in next && next.open !== null && next.open !== view.open && view.open === null;
    if (opening) listY.current = window.scrollY;
    window.history[opening ? 'pushState' : 'replaceState'](null, '', `${window.location.pathname}${searchOf(n, book.canApprove)}`);
    if (opening) pushed.current = true;
    setViewState(n);
  }, [view, book, narrow]);
  useEffect(() => {
    if (!book) return undefined;
    const onPop = () => { pushed.current = false; setViewState(viewOf(window.location.search, book.canApprove)); };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [book]);
  // The sheet opens at its top; Back puts the list where it was.
  const openId = view?.open ?? null;
  useEffect(() => {
    if (!narrow) return;
    if (openId) window.scrollTo(0, 0);
    else if (listY.current !== null) { window.scrollTo(0, listY.current); listY.current = null; }
  }, [narrow, openId]);
  // On a phone the tabs scroll sideways inside their strip: the one shown is brought into it, so a link to To allocate
  // does not open with its own tab cut off at the edge. The page itself never moves.
  const tabsRef = useRef<HTMLElement | null>(null);
  const shownTab = view?.tab ?? null;
  useEffect(() => {
    const bring = () => {
      const strip = tabsRef.current;
      const on = strip?.querySelector<HTMLElement>('.pbk-tab.on');
      if (!strip || !on) return;
      const left = on.getBoundingClientRect().left - strip.getBoundingClientRect().left + strip.scrollLeft;
      if (left < strip.scrollLeft) strip.scrollLeft = left;
      else if (left + on.offsetWidth > strip.scrollLeft + strip.clientWidth) strip.scrollLeft = left + on.offsetWidth - strip.clientWidth;
    };
    bring();
    // The tabs widen when the page's font arrives after the first paint; measure again then.
    let live = true;
    void document.fonts?.ready.then(() => { if (live) bring(); });
    return () => { live = false; };
  }, [shownTab, book]);
  const backToList = useCallback(() => {
    if (pushed.current) window.history.back();
    else setView({ open: null });
  }, [setView]);
  useEffect(() => {
    if (!toast?.undo) return undefined;
    setLeft(Math.round(UNDO_MS / 1000));
    const t = window.setInterval(() => setLeft((n) => Math.max(0, n - 1)), 1000);
    return () => window.clearInterval(t);
  }, [toast]);

  const say = (t: Toast) => {
    setToast(t);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), UNDO_MS);
  };
  const changed = (rows: BookEntry[]) => setBook((b) => b && { ...b, entries: b.entries.map((e) => rows.find((r) => r.id === e.id) ?? e) });
  const note = (key: string, text: string | null) => setNotes((n) => { const c = { ...n }; if (text) c[key] = text; else delete c[key]; return c; });

  /** One press on an entry: the rows it changed shown at once, another approver's change shown in its place, a failure said. */
  const press = async (key: string, run: () => Promise<{ changed: BookEntry[] }>, done: (rows: BookEntry[]) => Toast | null, failed: string) => {
    note(key, null);
    try {
      const r = await run();
      changed(r.changed);
      const t = done(r.changed);
      if (t) say(t);
    } catch (e: unknown) {
      if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
      if (e instanceof PlaybooksError && e.moved) { changed([e.moved]); note(key, e.message); return; }
      note(key, e instanceof PlaybooksError && e.status === 403 ? `${failed} ${e.message}.` : `${failed} Cobrain could not save it. Nothing changed.`);
    }
  };

  const rows = useMemo(() => (book && view ? rowsOf(book, view.family) : []), [book, view]);
  const yours = useMemo(() => (book ? yoursOf(book) : new Set<string>()), [book]);

  if (!book || !view) {
    return (
      <div className="pbk">
        <div className="pbk-head"><h1>Playbooks</h1></div>
        {error ? (
          <div className="pbk-empty pbk-box" role="alert">
            <p><b>Cobrain could not read the playbook just now.</b></p>
            <p className="pbk-err">The list did not load. Nothing was changed.</p>
            <div className="pbk-acts"><ActionButton reading type="button" className="opt lead" onClick={() => void load()}>Try again</ActionButton></div>
          </div>
        ) : (
          <section className="pbk-split one">
            <div className="pbk-ls" role="status" aria-label="Reading the playbook">
              <div className="pbk-lh"><span>Objection</span><span>Opportunities</span></div>
              <ul className="pbk-skel">{[0, 1, 2, 3, 4, 5].map((i) => <li key={i}><i /><i /></li>)}</ul>
            </div>
          </section>
        )}
      </div>
    );
  }

  const c = counts(rows, view, yours, book);
  const inScope = scoped(rows, view, yours);
  const listed = ordered(inScope.filter((r) => inTab(r, view.tab)));
  const retired = rows.filter((r) => r.o.state === 'retired');
  const picked = rows.find((r) => r.o.id === view.open) ?? (narrow ? null : listed[0] ?? null);
  const showList = !narrow || picked === null;
  const sheetPage = narrow && picked !== null && view.tab !== 'rules';
  const ctx: Ctx = { book, store: tenant, view, setView, press, notes, say, go, rows, reload: load, suggest: () => setSuggesting(true) };
  const at = (d: Date) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

  const tabs: Array<[Tab, string, number | null]> = book.canApprove
    ? [['all', 'All', c.tabs.all], ['waiting', 'To approve', c.tabs.waiting], ['approved', 'Approved', c.tabs.approved], ['words', 'Needs words', c.tabs.words], ['allocate', 'To allocate', c.tabs.allocate], ['rules', 'Fallback rules: what may be conceded', null]]
    : [['all', 'All', c.tabs.all], ['approved', 'Approved', c.tabs.approved], ['mine', 'Your suggestions', c.tabs.mine], ['rules', 'Fallback rules', null]];
  const companies = [...new Set(rows.flatMap((r) => r.o.raisedBy.map((x) => x.account)))].sort((a, b) => a.localeCompare(b));

  return (
    <div className="pbk">
      {error ? <p className="pbk-err" role="alert">Cobrain could not read the playbook again just now. This is the list as it last read{readAt ? `, at ${at(readAt)}` : ''}. <ActionButton reading type="button" className="pbk-link" onClick={() => void load()}>Try again</ActionButton></p> : null}
      {sheetPage ? null : <>
      <div className="pbk-head">
        <h1>Playbooks</h1>
        <span className="pbk-meta">
          {book.canApprove
            ? <><span><b>{c.objections}</b> objections on <b>{c.opportunities}</b> opportunities</span>{c.waiting > 0 ? <span>{waitingWords(c.waiting - c.suggestions, c.suggestions)} for you or another approver</span> : <span>Nothing waits for an approver</span>}</>
            : <><span><b>{view.family === 'yours' && yours.size > 0 ? c.tabs.all : c.objections}</b> objections{view.family === 'yours' && yours.size > 0 ? ' in your products' : ''}</span>{c.mine > 0 ? <span><b>{c.mine}</b> of your suggestions {c.mine === 1 ? 'waits' : 'wait'} for an approver</span> : null}</>}
        </span>
        <span className="pbk-tools">
          <label className="pbk-find">
            <svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="7" cy="7" r="4.5" /><path d="M10.5 10.5L14 14" /></svg>
            <span className="pbk-sr">Find an objection or a client's words</span>
            <ActionInput reading type="search" value={view.q} placeholder="Find an objection or a client's words" onChange={(e) => setView({ q: e.target.value })} />
          </label>
          <ActionButton type="button" className="pbk-btn" aria-expanded={suggesting} onClick={() => setSuggesting((s) => !s)}>Suggest an answer</ActionButton>
        </span>
      </div>
      {suggesting ? <SuggestPanel ctx={ctx} objectionId={picked?.o.id ?? null} onClose={() => setSuggesting(false)} /> : null}
      <div className="pbk-bar">
        <nav className="pbk-tabs" aria-label="Show" ref={tabsRef}>
          {tabs.map(([t, label, n]) => (
            <ActionButton reading key={t} type="button" className={view.tab === t ? 'pbk-tab on' : 'pbk-tab'} aria-current={view.tab === t ? 'true' : undefined} onClick={() => setView({ tab: t })}>
              {label}{n === null ? null : <span className="c">{n}</span>}
            </ActionButton>
          ))}
        </nav>
        {view.tab === 'allocate' ? null : <div className="pbk-pills" role="group" aria-label="Products">
          {!book.canApprove && yours.size > 0 ? <ActionButton reading type="button" className={view.family === 'yours' ? 'pbk-pl on' : 'pbk-pl'} aria-pressed={view.family === 'yours'} onClick={() => setView({ family: 'yours' })}>Yours</ActionButton> : null}
          <ActionButton reading type="button" className={view.family === null ? 'pbk-pl on' : 'pbk-pl'} aria-pressed={view.family === null} onClick={() => setView({ family: null })}>All</ActionButton>
          {book.families.filter((f) => c.families[f]! > 0 || view.family === f).map((f) => (
            <ActionButton reading key={f} type="button" className={view.family === f ? 'pbk-pl on' : 'pbk-pl'} aria-pressed={view.family === f} onClick={() => setView({ family: f })}>{f} <span className="c">{c.families[f]}</span></ActionButton>
          ))}
          <label className={view.company ? 'pbk-pl pbk-co on' : 'pbk-pl pbk-co'}>
            <span className="pbk-sr">By company</span>
            <ActionSelect reading value={view.company ?? ''} onChange={(e) => setView({ company: e.target.value || null })}>
              <option value="">By company</option>
              {companies.map((a) => <option key={a} value={a}>{a}</option>)}
            </ActionSelect>
          </label>
        </div>}
      </div>
      </>}

      {view.tab === 'rules' ? <Rules ctx={ctx} /> : view.tab === 'allocate' && book.canApprove ? <Allocate ctx={ctx} /> : (
        <section className={narrow ? 'pbk-split one' : 'pbk-split'}>
          {showList ? (
            <div className="pbk-ls">
              <div className="pbk-lh"><span>Objection</span><span>Opportunities</span></div>
              {listed.length > 0 ? (
                <ul aria-label="Objections, most raised first">
                  {listed.map((r) => (
                    <li key={r.o.id}>
                      <ActionButton reading type="button" className={picked?.o.id === r.o.id && !narrow ? 'pbk-li sel' : 'pbk-li'} aria-current={picked?.o.id === r.o.id && !narrow ? 'true' : undefined} onClick={() => setView({ open: r.o.id })}>
                        <span>
                          <span className="nm">{r.o.name}</span>
                          <span className="sm">{r.families.join(', ') || 'Every product'}</span>
                          <span className="gst">{r.gist && r.gist.status !== 'rejected' ? r.gist.body : 'Nobody has written our answer yet'}</span>
                        </span>
                        <span>
                          <span className="n">{r.raisedOn}</span>
                          <span className={stateWord(r, book.canApprove) === 'Approved' ? 'w ok' : 'w'}>{stateWord(r, book.canApprove)}</span>
                        </span>
                      </ActionButton>
                    </li>
                  ))}
                </ul>
              ) : <Empty ctx={ctx} />}
              {retired.length > 0 ? (
                <details className="pbk-retired">
                  <summary>Retired: {retired.length}</summary>
                  <ul>{retired.map((r) => <li key={r.o.id}><ActionButton reading type="button" className="pbk-li" onClick={() => setView({ open: r.o.id })}><span><span className="nm">{r.o.name}</span><span className="sm">Retired{r.o.stateBy ? ` by ${r.o.stateBy}` : ''}{r.o.stateAt ? `, ${dayOf(r.o.stateAt)}` : ''}: no longer used anywhere</span></span><span><span className="w">Retired</span></span></ActionButton></li>)}</ul>
                </details>
              ) : null}
            </div>
          ) : null}
          {picked ? <Sheet key={picked.o.id} ctx={ctx} r={picked} back={narrow ? backToList : null} /> : null}
          {!picked && !narrow ? <div className="pbk-sh" /> : null}
        </section>
      )}

      <div className="pbk-toast-slot" aria-live="polite">
        {toast ? (
          <div className="pbk-toast">
            <span>{toast.text}</span>
            {toast.undo ? <><ActionButton type="button" onClick={() => { const u = toast.undo!; setToast(null); void u(); }}>Undo</ActionButton><span className="pbk-left">{left} {left === 1 ? 'second' : 'seconds'}</span></> : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

interface Ctx {
  book: PlaybookBook; store: string; view: View; setView: (v: Partial<View>) => void; notes: Notes; rows: Row[];
  press: (key: string, run: () => Promise<{ changed: BookEntry[] }>, done: (rows: BookEntry[]) => Toast | null, failed: string) => Promise<void>;
  say: (t: Toast) => void; go: (path: string) => void; reload: () => Promise<void>; suggest: () => void;
}

/** "2 answers and client lines and 1 suggestion wait", the head's count said as what it counts. */
function waitingWords(entries: number, suggestions: number): ReactNode {
  const parts: ReactNode[] = [];
  if (entries > 0) parts.push(<span key="e"><b>{entries}</b> {entries === 1 ? 'answer or client line' : 'answers and client lines'}</span>);
  if (suggestions > 0) parts.push(<span key="s"><b>{suggestions}</b> {suggestions === 1 ? 'suggestion' : 'suggestions'}</span>);
  return <>{parts.flatMap((x, i) => (i > 0 ? [' and ', x] : [x]))} {entries + suggestions > 1 ? 'wait' : 'waits'}</>;
}

function Empty({ ctx }: { ctx: Ctx }) {
  const { view, rows, setView, book } = ctx;
  if (view.q.trim()) {
    const near = closest(rows.filter((r) => r.o.state !== 'retired'), view.q);
    return (
      <div className="pbk-empty">
        <p><b>Nothing matches “{view.q.trim()}”.</b>{near ? <> Closest: {near.o.name}, raised on {near.raisedOn} {near.raisedOn === 1 ? 'opportunity' : 'opportunities'}.</> : null}</p>
        <div className="pbk-acts">
          <ActionButton type="button" className="opt lead" onClick={ctx.suggest}>Suggest an answer</ActionButton>
          {near ? <ActionButton reading type="button" className="opt" onClick={() => setView({ q: '', open: near.o.id, tab: 'all' })}>Open {near.o.name}</ActionButton> : null}
        </div>
      </div>
    );
  }
  if (view.tab === 'waiting') return <div className="pbk-empty"><p><b>Nothing waits for you.</b> Cobrain brings the next one here when it hears it.</p></div>;
  if (view.tab === 'mine') return <div className="pbk-empty"><p>You have no suggestion waiting. Suggest an answer, or better words for one, and an approver decides.</p></div>;
  if (book.objections.length === 0) return <div className="pbk-empty"><p><b>No objection heard yet.</b> Cobrain adds one when a client pushes back on a call, in mail, in Teams or in a note, and an approver says yes before it is used.</p></div>;
  return <div className="pbk-empty"><p>No objection here under this filter.</p><ActionButton reading type="button" className="pbk-link" onClick={() => setView({ family: null, company: null, tab: 'all' })}>Show every objection</ActionButton></div>;
}

/** "approved by Conor, 4 Oct", "not approved yet", or the rejection or retirement with who and when. */
function stamp(e: BookEntry): string {
  if (e.status === 'approved') return `approved${e.approvedBy ? ` by ${e.approvedBy}` : ''}${e.approvedAt ? `, ${dayOf(e.approvedAt)}` : ''}`;
  if (e.status === 'rejected') return `rejected${e.statusBy ? ` by ${e.statusBy}` : ''}${e.statusAt ? `, ${dayOf(e.statusAt)}` : ''}`;
  if (e.status === 'retired') return `retired${e.statusBy ? ` by ${e.statusBy}` : ''}${e.statusAt ? `, ${dayOf(e.statusAt)}` : ''}`;
  if (e.status === 'gap' || e.status === 'open') return 'needs words';
  return 'not approved yet';
}
const opps = (n: number) => `${n} ${n === 1 ? 'opportunity' : 'opportunities'}`;

function Sheet({ ctx, r, back }: { ctx: Ctx; r: Row; back: (() => void) | null }) {
  const { book } = ctx;
  const [why, setWhy] = useState(false);
  const families = new Map<string | null, Line[]>();
  for (const l of r.lines) families.set(l.line.family, [...(families.get(l.line.family) ?? []), l]);
  const retire = (act: 'retire' | 'restore') => void playbooksApi.objection(ctx.store, r.o.id, act).then(async () => {
    await ctx.reload();
    if (act === 'retire') ctx.say({ text: `${r.o.name} is retired: used nowhere from now.`, undo: async () => { await playbooksApi.objection(ctx.store, r.o.id, 'restore'); await ctx.reload(); } });
  }, (e: unknown) => ctx.say({ text: e instanceof PlaybooksError ? `Not changed: ${e.message}.` : 'Cobrain could not save it. Nothing changed.' }));
  // Shane's objections carry their answer as replies under the client's line (pipeline K5): the history starts at the line.
  const said = r.answers[0] ?? null;
  const first = said ?? r.lines[0]?.line ?? null;
  return (
    <div className="pbk-sh">
      {back ? <ActionButton reading type="button" className="pbk-back" onClick={back}>Back to the list</ActionButton> : null}
      <div className="pbk-hd"><h2>{r.o.name}</h2><span className="m">raised on {opps(r.raisedOn)}</span></div>
      <Product ctx={ctx} r={r} />
      {r.o.state === 'retired' ? (
        <div className="pbk-sec"><p className="pbk-tx">Retired{r.o.stateBy ? ` by ${r.o.stateBy}` : ''}{r.o.stateAt ? `, ${dayOf(r.o.stateAt)}` : ''}: no longer used anywhere.</p>
          {book.canApprove ? <div className="pbk-acts"><ActionButton type="button" className="opt" onClick={() => retire('restore')}>Bring it back</ActionButton></div> : null}</div>
      ) : null}

      {r.answers.length > 0 ? r.answers.map((a) => <Answer key={a.id} ctx={ctx} r={r} a={a} />) : r.lines.length > 0 ? null : (
        <div className="pbk-sec">
          <h3>Our usual answer, every product <span className="st">needs words</span></h3>
          <p className="pbk-tx none">Nobody has written our answer yet.</p>
          <WriteAnswer ctx={ctx} r={r} />
        </div>
      )}
      {r.suggestions.filter((s) => s.kind !== 'better_words' || !r.answers.some((a) => a.id === s.targetEntryId)).map((s) => <Suggestion key={s.id} ctx={ctx} s={s} current={null} />)}

      {book.canApprove ? [...families.entries()].map(([family, lines]) => <Lines key={family ?? 'all'} ctx={ctx} r={r} family={family} lines={lines} />)
        : <><Usable r={r} /><Said r={r} /></>}
      {book.canApprove && r.retiredLines.length > 0 ? <RetiredLines ctx={ctx} lines={r.retiredLines} /> : null}

      {r.o.raisedBy.length > 0 ? (
        <div className="pbk-sec">
          <h3>Raised by</h3>
          <div className="pbk-mini">{r.o.raisedBy.map((x) => <a key={x.dealId} href={`/deals?open=${encodeURIComponent(x.accountId)}`} onClick={(e) => { e.preventDefault(); ctx.go(`/deals?open=${encodeURIComponent(x.accountId)}`); }}>{x.account}, {x.family}</a>)}</div>
        </div>
      ) : null}

      <div className="pbk-sec">
        <h3>History</h3>
        <ul className="pbk-hist">
          {first ? <li>Added {dayOf(first.createdAt)}{sourceWords(first.source) ? ` ${sourceWords(first.source)!.replace(/^From/, 'from')}` : ''}.{first.status === 'approved' ? ` Approved${first.approvedBy ? ` by ${first.approvedBy}` : ''}${first.approvedAt ? ` on ${dayOf(first.approvedAt)}` : ''}, in use since.` : first.status === 'rejected' ? ` Rejected${first.statusBy ? ` by ${first.statusBy}` : ''}${first.statusAt ? ` on ${dayOf(first.statusAt)}` : ''}.` : ' Not approved yet.'}{first.version > 1 ? ` Changed ${first.version - 1} ${first.version === 2 ? 'time' : 'times'} since it was added.` : ''}</li> : <li>No answer has been written for it.</li>}
          {book.canApprove && r.lines.length > 0 ? <li>{r.lines.length} {r.lines.length === 1 ? 'client line' : 'client lines'}, {r.lines.filter((l) => l.line.status === 'approved').length} approved.</li> : null}
        </ul>
      </div>

      <div className="pbk-mini pbk-foot">
        {book.canApprove && r.o.state !== 'retired' ? <ActionButton type="button" className="pbk-link" onClick={() => retire('retire')}>Retire it everywhere</ActionButton> : null}
        <ActionButton reading type="button" className="pbk-link" aria-expanded={why} onClick={() => setWhy((w) => !w)}>Why is this here?</ActionButton>
      </div>
      {why ? <p className="pbk-why">{r.raisedOn > 0 ? `Clients raised it on ${opps(r.raisedOn)} the team holds` : 'No opportunity carries it yet'}{said?.source && sourceWords(said.source) ? `; the answer came ${sourceWords(said.source)!.replace(/^From/, 'from')}` : ''}{r.lines.some((l) => /\.pptx/i.test(l.line.source ?? '')) ? "; it is a client's line from Shane's deck, with his replies" : ''}.</p> : null}
    </div>
  );
}

function Answer({ ctx, r, a }: { ctx: Ctx; r: Row; a: BookEntry }) {
  const { book } = ctx;
  const [editing, setEditing] = useState<string | null>(null);
  // Another approver's change (or one's own) replaces the row: the editor closes so the words now in use show.
  useEffect(() => { setEditing(null); }, [a.version]);
  const key = a.id;
  const noWords = a.status === 'gap' || a.status === 'open';
  const act = (what: EntryAct) => ctx.press(key, () => playbooksApi.act(ctx.store, a.id, what, a.version), (rows) => {
    const now = rows.find((x) => x.id === a.id)!;
    const text = what === 'approve' ? `Approved. On ${opps(r.raisedOn)} from now.` : what === 'reject' ? 'Rejected: the deals show no answer.' : what === 'retire' ? 'Retired.' : 'Put back.';
    return what === 'restore' ? { text } : { text, undo: () => ctx.press(key, () => playbooksApi.act(ctx.store, a.id, 'restore', now.version), () => null, 'Not undone:') };
  }, what === 'approve' ? 'Not approved:' : 'Not changed:');
  const save = (e: FormEvent) => {
    e.preventDefault();
    const words = (editing ?? '').trim();
    if (!words) return;
    void ctx.press(key, () => playbooksApi.words(ctx.store, a.id, words, a.version), () => { setEditing(null); return { text: 'Approved as written. Used from now.' }; }, 'Not approved:');
  };
  const suggestion = r.suggestions.find((s) => s.kind === 'better_words' && s.targetEntryId === a.id) ?? null;
  return (
    <div className="pbk-sec">
      <h3>Our usual answer, {a.family ?? 'every product'} <span className="st">{book.canApprove && waits(a) ? 'to approve' : stamp(a)}</span></h3>
      {editing !== null ? (
        <form onSubmit={save}>
          <label className="pbk-sr" htmlFor={`w-${a.id}`}>Our usual answer</label>
          <ActionTextarea id={`w-${a.id}`} className="pbk-area" value={editing} onChange={(e) => setEditing(e.target.value)} rows={4} />
          <p className="pbk-src">Used from the moment you approve. The old words are kept.</p>
          <div className="pbk-acts"><ActionButton type="submit" className="opt lead">Approve as written</ActionButton><ActionButton type="button" className="opt" onClick={() => setEditing(null)}>Cancel</ActionButton></div>
        </form>
      ) : <p className={a.status === 'rejected' ? 'pbk-tx struck' : noWords ? 'pbk-tx none' : 'pbk-tx'}>{a.body}</p>}
      {editing === null ? (
        <p className="pbk-src">
          {sourceWords(a.source) ? `${sourceWords(a.source)}. ` : ''}
          {book.canApprove
            ? a.status === 'approved' ? `On the ${opps(r.raisedOn)} and in Cobrain's write-ups of them.` : a.status === 'rejected' ? 'The deals show no answer.' : `Once approved it shows on the ${opps(r.raisedOn)} and in Cobrain's write-ups of them.`
            : a.status === 'approved' ? null : 'Deals show it marked not approved; on a call Cobrain uses approved words only.'}
        </p>
      ) : null}
      {ctx.notes[key] ? <p className="pbk-err">{ctx.notes[key]}</p> : null}
      {editing === null ? (
        book.canApprove ? (
          <div className="pbk-acts">
            {noWords ? <ActionButton type="button" className="opt lead" onClick={() => setEditing('')}>Write the words</ActionButton> : null}
            {waits(a) ? <><ActionButton type="button" className="opt lead" onClick={() => void act('approve')}>Approve</ActionButton><ActionButton type="button" className="opt" onClick={() => setEditing(a.body)}>Edit</ActionButton><ActionButton type="button" className="opt" onClick={() => void act('reject')}>Reject</ActionButton></> : null}
            {a.status === 'approved' ? <><ActionButton type="button" className="opt" onClick={() => setEditing(a.body)}>Edit</ActionButton><ActionButton type="button" className="opt" onClick={() => void act('reject')}>Reject</ActionButton></> : null}
            {a.status === 'rejected' ? <><ActionButton type="button" className="opt" onClick={() => setEditing(a.body)}>Write new words</ActionButton><ActionButton type="button" className="opt" onClick={() => void act('restore')}>Bring it back</ActionButton></> : null}
          </div>
        ) : (
          <div className="pbk-acts">
            {a.status === 'approved' ? <ActionButton type="button" className="opt lead" onClick={() => { void navigator.clipboard?.writeText(a.body).then(() => ctx.say({ text: 'Copied.' }), () => ctx.say({ text: 'Your browser did not let Cobrain copy it; select the words instead.' })); }}>Copy the words</ActionButton> : null}
            {!suggestion ? <SuggestWords ctx={ctx} a={a} /> : null}
          </div>
        )
      ) : null}
      {suggestion ? <Suggestion ctx={ctx} s={suggestion} current={a} /> : null}
    </div>
  );
}

/** An approver writes the first answer for a type; anyone else suggests one. */
function WriteAnswer({ ctx, r }: { ctx: Ctx; r: Row }) {
  const [text, setText] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const label = ctx.book.canApprove ? 'Write the answer' : 'Suggest an answer';
  if (text === null) return <div className="pbk-acts"><ActionButton type="button" className="opt" onClick={() => setText('')}>{label}</ActionButton></div>;
  const send = async (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    setFailed(null);
    try {
      const s = await playbooksApi.suggest(ctx.store, { kind: 'new_answer', objectionId: r.o.id, targetEntryId: null, family: null, body: text.trim() });
      if (ctx.book.canApprove) await playbooksApi.decide(ctx.store, s.suggestion.id, 'approve');
      await ctx.reload();
      setText(null);
      ctx.say({ text: ctx.book.canApprove ? `Approved as written. On ${opps(r.raisedOn)} from now.` : 'Sent to the approvers. You can change it until one decides.' });
    } catch (err: unknown) {
      setFailed(err instanceof PlaybooksError ? `Not sent: ${err.message}.` : 'Not sent: Cobrain could not save it. Nothing changed.');
    }
  };
  return (
    <form onSubmit={(e) => void send(e)}>
      <label className="pbk-sr" htmlFor={`n-${r.o.id}`}>{label}</label>
      <ActionTextarea id={`n-${r.o.id}`} className="pbk-area" value={text} onChange={(e) => setText(e.target.value)} rows={4} />
      {failed ? <p className="pbk-err">{failed}</p> : null}
      <div className="pbk-acts"><ActionButton type="submit" className="opt lead">{ctx.book.canApprove ? 'Approve as written' : 'Send to the approvers'}</ActionButton><ActionButton type="button" className="opt" onClick={() => setText(null)}>Cancel</ActionButton></div>
    </form>
  );
}

function SuggestWords({ ctx, a }: { ctx: Ctx; a: BookEntry }) {
  const [text, setText] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  if (text === null) return <ActionButton type="button" className="opt" onClick={() => setText(a.body)}>Suggest better words</ActionButton>;
  const send = async (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await playbooksApi.suggest(ctx.store, { kind: 'better_words', objectionId: a.objectionId, targetEntryId: a.id, family: a.family, body: text.trim() });
      await ctx.reload();
      setText(null);
      ctx.say({ text: 'Sent to the approvers. You can change it until one decides.' });
    } catch (err: unknown) {
      setFailed(err instanceof PlaybooksError ? `Not sent: ${err.message}.` : 'Not sent: Cobrain could not save it. Nothing changed.');
    }
  };
  return (
    <form className="pbk-wide" onSubmit={(e) => void send(e)}>
      <label className="pbk-sr" htmlFor={`s-${a.id}`}>Better words</label>
      <ActionTextarea id={`s-${a.id}`} className="pbk-area" value={text} onChange={(e) => setText(e.target.value)} rows={4} />
      {failed ? <p className="pbk-err">{failed}</p> : null}
      <div className="pbk-acts"><ActionButton type="submit" className="opt lead">Send to the approvers</ActionButton><ActionButton type="button" className="opt" onClick={() => setText(null)}>Cancel</ActionButton></div>
    </form>
  );
}

/** A suggestion waiting: the suggester's own (edit or withdraw), or one for an approver to decide beside the words now in use. */
function Suggestion({ ctx, s, current }: { ctx: Ctx; s: PlaybookBook['suggestions'][number]; current: BookEntry | null }) {
  const [text, setText] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const run = async (fn: () => Promise<unknown>, done: string) => {
    setFailed(null);
    try { await fn(); await ctx.reload(); setText(null); ctx.say({ text: done }); } catch (e: unknown) {
      setFailed(e instanceof PlaybooksError ? `Not changed: ${e.message}.` : 'Not changed: Cobrain could not save it. Nothing changed.');
    }
  };
  const what = s.kind === 'better_words' ? 'Better words' : s.kind === 'new_rule' ? 'A rule' : s.kind === 'new_reply' ? 'A reply'
    : s.kind === 'fill_gap' ? 'Words for a reply' : `An answer, ${s.family ?? 'every product'}`;
  // A reply is shown under the client's line it answers, and words for a reply beside the reply waiting for them. What
  // Cobrain heard is never a suggestion on the list since K8: it waits in To allocate, an item of its own.
  const under = current ?? (s.kind === 'new_reply' || s.kind === 'fill_gap' ? ctx.book.entries.find((e) => e.id === s.targetEntryId) ?? null : null);
  const underWords = s.kind === 'new_reply' ? "The client's line" : s.kind === 'fill_gap' ? 'Waiting for words' : 'Now';
  if (s.mine && !ctx.book.canApprove) {
    return (
      <div className="pbk-sug">
        <h4>Your suggestion, waiting <span className="st">with the approvers</span></h4>
        {text === null ? <p className="pbk-tx">“{s.body}”</p> : <ActionTextarea className="pbk-area" aria-label="Your suggestion" value={text} onChange={(e) => setText(e.target.value)} rows={4} />}
        <p className="pbk-src">Sent {dayOf(s.createdAt)} to {ctx.book.approvers.join(', ')}.</p>
        {failed ? <p className="pbk-err">{failed}</p> : null}
        <div className="pbk-mini">
          {text === null ? <ActionButton type="button" className="pbk-link" onClick={() => setText(s.body)}>Edit it</ActionButton>
            : <ActionButton type="button" className="pbk-link" onClick={() => void run(() => playbooksApi.changeSuggestion(ctx.store, s.id, { body: text }), 'Changed. Still with the approvers.')}>Save it</ActionButton>}
          <ActionButton type="button" className="pbk-link" onClick={() => void run(() => playbooksApi.changeSuggestion(ctx.store, s.id, { withdraw: true }), 'Withdrawn.')}>Withdraw it</ActionButton>
        </div>
      </div>
    );
  }
  if (!ctx.book.canApprove) return null;
  return (
    <div className="pbk-sug">
      <h4>A suggestion to decide <span className="st">{what}</span></h4>
      {under ? <><p className="pbk-src">{underWords}</p><p className="pbk-tx">“{under.body}”</p></> : null}
      <p className="pbk-src">Suggested by {s.by ?? 'a colleague'}, {dayOf(s.createdAt)}</p>
      {text === null ? <p className="pbk-tx">“{s.body}”</p> : <ActionTextarea className="pbk-area" aria-label="The suggestion's words" value={text} onChange={(e) => setText(e.target.value)} rows={4} />}
      {failed ? <p className="pbk-err">{failed}</p> : null}
      <div className="pbk-acts">
        {text === null ? <>
          <ActionButton type="button" className="opt lead" onClick={() => void run(() => playbooksApi.decide(ctx.store, s.id, 'approve'), 'Approved. Used from now.')}>Approve the suggestion</ActionButton>
          <ActionButton type="button" className="opt" onClick={() => setText(s.body)}>Edit, then approve</ActionButton>
          <ActionButton type="button" className="opt" onClick={() => void run(() => playbooksApi.decide(ctx.store, s.id, 'reject'), 'Kept ours.')}>Keep ours</ActionButton>
        </> : <>
          <ActionButton type="button" className="opt lead" onClick={() => void run(() => playbooksApi.decide(ctx.store, s.id, 'approve', text), 'Approved as written. Used from now.')}>Approve as written</ActionButton>
          <ActionButton type="button" className="opt" onClick={() => setText(null)}>Cancel</ActionButton>
        </>}
      </div>
    </div>
  );
}

/** The product on the sheet's head, and Change product for an approver (U273): a Deals family or every product. */
function Product({ ctx, r }: { ctx: Ctx; r: Row }) {
  const { book } = ctx;
  const [editing, setEditing] = useState(false);
  const [to, setTo] = useState(r.o.family ?? '');
  const [failed, setFailed] = useState<string | null>(null);
  const may = book.canApprove && r.o.allocation === 'listed' && r.o.state !== 'retired';
  const words = r.o.family ?? 'Every product';
  const save = async () => {
    setFailed(null);
    const before = r.o.family;
    const next = to || null;
    if (next === before) { setEditing(false); return; }
    try {
      await playbooksApi.product(ctx.store, r.o.id, next);
      setEditing(false);
      await ctx.reload();
      ctx.say({ text: `${r.o.name}: ${next ?? 'every product'} from now.`, undo: async () => { await playbooksApi.product(ctx.store, r.o.id, before); await ctx.reload(); } });
    } catch (e: unknown) {
      setFailed(e instanceof PlaybooksError ? `Not changed: ${e.message}.` : 'Not changed: Cobrain could not save it. Nothing changed.');
    }
  };
  return (
    <div className="pbk-chips">
      {r.families.length > 0 ? r.families.map((f) => <span key={f} className="pbk-chip">{f}</span>) : may ? <span className="pbk-chip">{words}</span> : null}
      {may && !editing ? <ActionButton type="button" className="pbk-link" onClick={() => { setTo(r.o.family ?? ''); setEditing(true); }}>Change product</ActionButton> : null}
      {editing ? (
        <span className="pbk-move">
          <label><span className="pbk-sr">Product</span>
            <ActionSelect value={to} onChange={(e) => setTo(e.target.value)}>
              <option value="">Every product</option>
              {book.families.map((f) => <option key={f} value={f}>{f}</option>)}
            </ActionSelect>
          </label>
          <ActionButton type="button" className="opt lead" onClick={() => void save()}>Save</ActionButton>
          <ActionButton type="button" className="opt" onClick={() => { setEditing(false); setFailed(null); }}>Cancel</ActionButton>
        </span>
      ) : null}
      {failed ? <p className="pbk-err">{failed}</p> : null}
    </div>
  );
}

/** To allocate (pipeline K6; U274, U275): each item Cobrain picked up, with where it came from and the deals that raised it;
 *  an approver puts it under Cobrain's best match, under another of the list's objections, makes it an objection of its
 *  own, or bins it. Placing is the approval, and each can be undone. */
function Allocate({ ctx }: { ctx: Ctx }) {
  const { book, view } = ctx;
  const items = waitingOf(book).filter((i) => (view.company === null || (i.o ? i.o.raisedBy.some((x) => x.account === view.company) : i.s?.heard?.account === view.company)) && itemMatches(i, view.q));
  const done = allocatedOf(book);
  return (
    <section className="pbk-alloc" aria-label="To allocate">
      <p className="pbk-src">Cobrain picked these up from the SBC show notes and the team's conversations. Nothing here is used until one of the approvers places it, and placing it is the approval.</p>
      {items.length > 0 ? <ul className="pbk-items">{items.map((i) => <li key={i.key}><ItemCard ctx={ctx} i={i} /></li>)}</ul>
        : <div className="pbk-empty"><p><b>Nothing waits to be allocated.</b> Cobrain brings the next one here when it hears it.</p></div>}
      {done.length > 0 ? (
        <details className="pbk-retired">
          <summary>Allocated: {done.length}</summary>
          <ul>{done.map((a) => <li key={a.key}><AllocatedLine ctx={ctx} a={a} /></li>)}</ul>
        </details>
      ) : null}
    </section>
  );
}

function ItemCard({ ctx, i }: { ctx: Ctx; i: Item }) {
  const { book } = ctx;
  const [open, setOpen] = useState<'another' | 'new' | null>(null);
  const [under, setUnder] = useState('');
  const [family, setFamily] = useState(i.s?.family ?? '');
  const [words, setWords] = useState(i.s ? i.s.answered ?? '' : i.answer?.body ?? '');
  const [name, setName] = useState(nameOf(i));
  const [failed, setFailed] = useState<string | null>(null);
  const list = listedInUse(book);
  const act = async (how: Press, text: string) => {
    setFailed(null);
    try {
      const undo = await pressItem(ctx.store, i, how);
      await ctx.reload();
      ctx.say({ text, undo: async () => { await undo(); await ctx.reload(); } });
    } catch (e: unknown) {
      setFailed(e instanceof PlaybooksError ? `Not changed: ${e.message}.` : 'Not changed: Cobrain could not save it. Nothing changed.');
    }
  };
  const placeUnder = (o: BookObjection) => void act({ place: o.id }, placedSaid(i, o.name));
  const meeting = i.s?.heard?.meetingId ? `/meetings/${encodeURIComponent(i.s.heard.meetingId)}` : null;
  const makeIt = () => {
    // Renamed in the same press when the approver changed the name (B2059); the undo puts the old name back.
    const made = newObjection(i, name, family, words);
    if (!made) { setFailed('Not changed: give it a name.'); return; }
    void act(made.how, made.said);
  };
  return (
    <div className="pbk-sug pbk-heard pbk-item">
      <h4>{i.s ? `“${i.name}”` : i.name} <span className="st">To allocate</span></h4>
      <p className="pbk-src">{itemSource(i)}{raisedWords(i)}</p>
      {i.answer ? <><p className="pbk-src">The usual answer it came with</p><p className="pbk-tx">“{i.answer.body}”</p></> : null}
      {i.s?.answered ? <><p className="pbk-src">Our side answered</p><p className="pbk-tx">“{i.s.answered}”</p></> : null}
      {i.said.slice(0, 2).map((x) => <p key={x.dealId} className="pbk-tx"><span className="pbk-src">{x.account}: </span>“{x.said}”</p>)}
      <p className="pbk-src pbk-match"><MatchLine i={i} /></p>
      {open === 'another' ? (
        <label className="pbk-move"><span>Put it under</span>
          <ActionSelect value={under} onChange={(e) => setUnder(e.target.value)}>
            <option value="">Pick one of the list</option>
            {list.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </ActionSelect>
          <ActionButton type="button" className="opt lead" disabled={!under} onClick={() => { const o = list.find((x) => x.id === under); if (o) placeUnder(o); }}>Put under</ActionButton>
        </label>
      ) : null}
      {open === 'new' ? (
        <div className="pbk-new">
          <label className="pbk-move pbk-words"><span>Name</span>
            <ActionInput className="pbk-area" type="text" value={name} maxLength={NAME_MAX} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="pbk-move"><span>Product</span>
            <ActionSelect value={family} onChange={(e) => setFamily(e.target.value)}>
              <option value="">Every product</option>
              {book.families.map((f) => <option key={f} value={f}>{f}</option>)}
            </ActionSelect>
          </label>
          <label className="pbk-move pbk-words"><span>Our answer</span>
            <ActionTextarea className="pbk-area" rows={3} value={words} onChange={(e) => setWords(e.target.value)} placeholder="Leave empty if it needs words" />
          </label>
          <ActionButton type="button" className="opt lead" onClick={makeIt}>Make it an objection</ActionButton>
        </div>
      ) : null}
      {failed ? <p className="pbk-err">{failed}</p> : null}
      <div className="pbk-acts">
        {i.match ? <ActionButton type="button" className="opt lead" onClick={() => placeUnder(i.match!)}>Put under “{i.match.name}”</ActionButton> : null}
        <ActionButton type="button" className={!i.match && open === null ? 'opt lead' : 'opt'} aria-expanded={open === 'new'} onClick={() => setOpen((x) => (x === 'new' ? null : 'new'))}>New objection</ActionButton>
        <ActionButton type="button" className="opt" aria-expanded={open === 'another'} onClick={() => { setOpen((x) => (x === 'another' ? null : 'another')); setUnder(''); }}>Under another</ActionButton>
        <ActionButton type="button" className="opt" onClick={() => void act({ bin: true }, binnedSaid(i))}>Bin it</ActionButton>
      </div>
      {meeting ? <div className="pbk-mini"><a href={meeting} onClick={(e) => { e.preventDefault(); ctx.go(meeting); }}>Open the call</a></div> : null}
    </div>
  );
}

function AllocatedLine({ ctx, a }: { ctx: Ctx; a: Allocated }) {
  const [failed, setFailed] = useState<string | null>(null);
  const name = a.o ? a.o.name : `“${a.s.body}”`;
  const undo = async () => {
    setFailed(null);
    try {
      if (a.o) await playbooksApi.allocate(ctx.store, a.o.id, { undo: true });
      else await playbooksApi.allocateHeard(ctx.store, a.s.id, { undo: true });
      await ctx.reload();
      ctx.say({ text: ALLOCATE_SAID.again(name) });
    } catch (e: unknown) {
      setFailed(e instanceof PlaybooksError ? `Not undone: ${e.message}.` : 'Not undone: Cobrain could not save it.');
    }
  };
  return (
    <div className="pbk-li">
      <span><span className="nm">{name}</span><span className="sm">{allocatedWords(ctx.book, a)}</span>{failed ? <span className="pbk-err">{failed}</span> : null}</span>
      <span><ActionButton type="button" className="pbk-link" onClick={() => void undo()}>Undo</ActionButton></span>
    </div>
  );
}

/** What clients say for one family, with our reply under each line, word for word and never cut short (an approver's). */
function Lines({ ctx, r, family, lines }: { ctx: Ctx; r: Row; family: string | null; lines: Line[] }) {
  const [all, setAll] = useState(false);
  const shown = all ? lines : lines.slice(0, 3);
  const fromDeck = lines.filter((l) => /\.pptx/i.test(l.line.source ?? '')).length;
  return (
    <div className="pbk-sec">
      <h3>What clients say, and our reply <span className="st">{family ?? 'Every product'}</span>{fromDeck > 0 ? <span>{fromDeck} {fromDeck === 1 ? 'line' : 'lines'} from Shane's deck</span> : null}</h3>
      <ul className="pbk-lines">{shown.map((l) => <LineItem key={l.line.id} ctx={ctx} r={r} l={l} />)}</ul>
      {lines.length > 3 ? <div className="pbk-mini"><ActionButton reading type="button" className="pbk-link" aria-expanded={all} onClick={() => setAll((a) => !a)}>{all ? 'Show fewer' : `${lines.length - 3} more${lines.slice(3).every((l) => /\.pptx/i.test(l.line.source ?? '')) ? " from Shane's deck" : ''}`}</ActionButton></div> : null}
    </div>
  );
}

function LineItem({ ctx, r, l }: { ctx: Ctx; r: Row; l: Line }) {
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null);
  const versions = [l.line, ...l.replies, ...l.notes].map((x) => x.version).join(',');
  useEffect(() => { setEditing(null); }, [versions]);
  const [moving, setMoving] = useState(false);
  const [others, setOthers] = useState(false);
  const types = ctx.rows.filter((x) => x.o.id !== r.o.id && x.o.state !== 'retired').sort((a, b) => a.o.name.localeCompare(b.o.name));
  const act = (e: BookEntry, what: EntryAct) => ctx.press(e.id, () => playbooksApi.act(ctx.store, e.id, what, e.version), (rows) => {
    const now = rows.find((x) => x.id === e.id)!;
    const text = what === 'approve' ? (rows.length > 1 ? 'Approved, with the client\'s words above it. On calls from now.' : 'Approved. On calls from now.') : what === 'reject' ? 'Rejected.' : 'Retired.';
    return { text, undo: async () => { for (const x of rows) { const v = x.id === now.id ? now.version : x.version; await ctx.press(x.id, () => playbooksApi.act(ctx.store, x.id, 'restore', v), () => null, 'Not undone:'); } } };
  }, what === 'approve' ? 'Not approved:' : 'Not changed:');
  const save = (ev: FormEvent) => {
    ev.preventDefault();
    if (!editing || !editing.text.trim()) return;
    const e = [l.line, ...l.replies, ...l.notes].find((x) => x.id === editing.id)!;
    void ctx.press(e.id, () => playbooksApi.words(ctx.store, e.id, editing.text.trim(), e.version), () => { setEditing(null); return { text: 'Approved as written. Used from now.' }; }, 'Not approved:');
  };
  const move = (to: string) => {
    const name = ctx.rows.find((x) => x.o.id === to)?.o.name ?? 'the other objection';
    void ctx.press(l.line.id, () => playbooksApi.move(ctx.store, l.line.id, to, l.line.version), (rows) => {
      setMoving(false);
      const now = rows[0]!;
      return { text: `Moved to ${name}.`, undo: () => ctx.press(l.line.id, () => playbooksApi.move(ctx.store, l.line.id, r.o.id, now.version), () => null, 'Not undone:') };
    }, 'Not moved:');
  };
  const [reply, ...more] = l.replies;
  const replies = others ? l.replies : reply ? [reply] : [];
  const note = (id: string) => ctx.notes[id] ? <p className="pbk-err">{ctx.notes[id]}</p> : null;
  const editor = (e: BookEntry, label: string) => editing?.id === e.id ? (
    <form onSubmit={save}>
      <label className="pbk-sr" htmlFor={`e-${e.id}`}>{label}</label>
      <ActionTextarea id={`e-${e.id}`} className="pbk-area" value={editing.text} onChange={(ev) => setEditing({ id: e.id, text: ev.target.value })} rows={3} />
      <div className="pbk-acts"><ActionButton type="submit" className="opt lead">Approve as written</ActionButton><ActionButton type="button" className="opt" onClick={() => setEditing(null)}>Cancel</ActionButton></div>
    </form>
  ) : null;
  // The line's own actions sit at the end of its first reply's row, in the drawing's order: one row a line, not three.
  const lineActs = editing === null ? <>
    <ActionButton type="button" className="pbk-link" aria-expanded={moving} onClick={() => setMoving((m) => !m)}>Move to another objection</ActionButton>
    {more.length > 0 ? <ActionButton reading type="button" className="pbk-link" aria-expanded={others} onClick={() => setOthers((o) => !o)}>{others ? 'Show one reply' : more.length === 1 ? 'Show the other reply' : `Show the other ${more.length} replies`}</ActionButton> : null}
    <ActionButton type="button" className="pbk-link" onClick={() => setEditing({ id: l.line.id, text: l.line.body })}>Edit the client's words</ActionButton>
    {l.line.status !== 'retired' ? <ActionButton type="button" className="pbk-link" onClick={() => void act(l.line, 'retire')}>Retire the line</ActionButton> : null}
  </> : null;
  const source = sourceWords(l.line.source);
  return (
    <li>
      {editor(l.line, 'The client\'s words') ?? <div className="cw">“{l.line.body}”{l.line.status === 'approved' ? <span className="ok"> approved</span> : null}</div>}
      {source && editing?.id !== l.line.id ? <div className="pbk-src">{source}</div> : null}
      {note(l.line.id)}
      {replies.map((rp, i) => (
        <div key={rp.id}>
          {editor(rp, 'Our reply') ?? (
            rp.status === 'gap' || rp.status === 'open'
              ? <div className="say gap"><i>{rp.status === 'gap' ? 'Needs words' : 'Still open'}</i>{rp.body}</div>
              : <div className="say"><i>Say</i>{rp.body}{rp.status === 'approved' ? <span className="ok"> approved</span> : null}</div>
          )}
          {note(rp.id)}
          {editing?.id !== rp.id ? (
            <div className="pbk-mini">
              {rp.status === 'draft' ? <ActionButton type="button" className="pbk-link" onClick={() => void act(rp, 'approve')}>Approve the reply</ActionButton> : null}
              {rp.status === 'gap' || rp.status === 'open' ? <ActionButton type="button" className="pbk-link" onClick={() => setEditing({ id: rp.id, text: '' })}>Write the words</ActionButton>
                : <ActionButton type="button" className="pbk-link" onClick={() => setEditing({ id: rp.id, text: rp.body })}>Edit</ActionButton>}
              {rp.status !== 'rejected' ? <ActionButton type="button" className="pbk-link" onClick={() => void act(rp, 'reject')}>Reject</ActionButton> : <ActionButton type="button" className="pbk-link" onClick={() => void act(rp, 'restore')}>Bring it back</ActionButton>}
              {i === 0 ? lineActs : null}
            </div>
          ) : null}
        </div>
      ))}
      {replies.length === 0 && editing === null ? (
        <div className="pbk-mini">
          {l.line.status === 'draft' ? <ActionButton type="button" className="pbk-link" onClick={() => void act(l.line, 'approve')}>Approve the client's words</ActionButton> : null}
          {lineActs}
        </div>
      ) : null}
      {l.notes.map((n) => <div key={n.id} className="nt">Internal note, never shown on a call: {n.body}</div>)}
      {moving ? (
        <label className="pbk-move">
          <span>Move it to</span>
          <ActionSelect defaultValue="" onChange={(e) => { if (e.target.value) move(e.target.value); }}>
            <option value="" disabled>Choose an objection</option>
            {types.map((x) => <option key={x.o.id} value={x.o.id}>{x.o.name}</option>)}
          </ActionSelect>
        </label>
      ) : null}
    </li>
  );
}

/** An approver's way back for a client line retired by mistake: each one, with who retired it and when, and Bring it back. */
function RetiredLines({ ctx, lines }: { ctx: Ctx; lines: BookEntry[] }) {
  const back = (e: BookEntry) => ctx.press(e.id, () => playbooksApi.act(ctx.store, e.id, 'restore', e.version),
    () => ({ text: 'Brought back, as it was before it was retired.' }), 'Not brought back:');
  return (
    <details className="pbk-retired pbk-sec">
      <summary>Retired client lines: {lines.length}</summary>
      <ul className="pbk-lines">{lines.map((e) => (
        <li key={e.id}>
          <div className="cw struck">“{e.body}”</div>
          <div className="pbk-src">{stamp(e)}{e.family ? `, ${e.family}` : ''}</div>
          {ctx.notes[e.id] ? <p className="pbk-err">{ctx.notes[e.id]}</p> : null}
          <div className="pbk-mini"><ActionButton type="button" className="pbk-link" onClick={() => void back(e)}>Bring it back</ActionButton></div>
        </li>
      ))}</ul>
    </details>
  );
}

/** For anyone who is not an approver: the client lines an approver has approved, each with its approved replies, the words
 *  to use on a call. A line or reply not yet approved is not shown here: it is the team's note until then. */
function Usable({ r }: { r: Row }) {
  const lines = r.lines.filter((l) => l.line.status === 'approved' && l.replies.some((x) => x.status === 'approved'));
  if (lines.length === 0) return null;
  return (
    <div className="pbk-sec">
      <h3>What clients say, and our reply <span className="st">approved</span></h3>
      <ul className="pbk-lines">{lines.map((l) => (
        <li key={l.line.id}>
          <div className="cw">“{l.line.body}”{l.line.family ? <span className="pbk-src"> {l.line.family}</span> : null}</div>
          {l.replies.filter((x) => x.status === 'approved').map((x) => <div key={x.id} className="say"><i>Say</i>{x.body}</div>)}
        </li>
      ))}</ul>
    </div>
  );
}

/** For anyone who is not an approver: what clients said on the opportunities, word for word, with the account. */
function Said({ r }: { r: Row }) {
  const said = r.o.raisedBy.filter((x) => x.said);
  const [all, setAll] = useState(false);
  if (said.length === 0) return null;
  const shown = all ? said : said.slice(0, 2);
  return (
    <div className="pbk-sec">
      <h3>What clients said</h3>
      <ul className="pbk-lines">{shown.map((x) => <li key={x.dealId}><div className="pbk-src">{x.account}, {x.family}</div><div className="cw">“{x.said}”</div></li>)}</ul>
      {said.length > 2 ? <div className="pbk-mini"><ActionButton reading type="button" className="pbk-link" aria-expanded={all} onClick={() => setAll((a) => !a)}>{all ? 'Show fewer' : `${said.length - 2} more ${said.length - 2 === 1 ? 'opportunity' : 'opportunities'}`}</ActionButton></div> : null}
    </div>
  );
}

/** The head's Suggest an answer: which objection, which product, and the words. */
function SuggestPanel({ ctx, objectionId, onClose }: { ctx: Ctx; objectionId: string | null; onClose: () => void }) {
  const types = ctx.rows.filter((x) => x.o.state !== 'retired').sort((a, b) => a.o.name.localeCompare(b.o.name));
  const [to, setTo] = useState(objectionId ?? types[0]?.o.id ?? '');
  const [family, setFamily] = useState('');
  const [text, setText] = useState('');
  const [failed, setFailed] = useState<string | null>(null);
  const send = async (e: FormEvent) => {
    e.preventDefault();
    if (!to || !text.trim()) return;
    setFailed(null);
    try {
      await playbooksApi.suggest(ctx.store, { kind: 'new_answer', objectionId: to, targetEntryId: null, family: family || null, body: text.trim() });
      await ctx.reload();
      ctx.say({ text: 'Sent to the approvers. You can change it until one decides.' });
      onClose();
    } catch (err: unknown) {
      setFailed(err instanceof PlaybooksError ? `Not sent: ${err.message}.` : 'Not sent: Cobrain could not save it. Nothing changed.');
    }
  };
  return (
    <form className="pbk-panel" onSubmit={(e) => void send(e)}>
      <div className="pbk-row">
        <label><span>Objection</span><ActionSelect value={to} onChange={(e) => setTo(e.target.value)}>{types.map((x) => <option key={x.o.id} value={x.o.id}>{x.o.name}</option>)}</ActionSelect></label>
        <label><span>Product</span><ActionSelect value={family} onChange={(e) => setFamily(e.target.value)}><option value="">Every product</option>{ctx.book.families.map((f) => <option key={f} value={f}>{f}</option>)}</ActionSelect></label>
      </div>
      <label className="pbk-wide"><span>The words to say</span><ActionTextarea className="pbk-area" value={text} onChange={(e) => setText(e.target.value)} rows={4} /></label>
      {failed ? <p className="pbk-err">{failed}</p> : null}
      <div className="pbk-acts"><ActionButton type="submit" className="opt lead">Send to the approvers</ActionButton><ActionButton type="button" className="opt" onClick={onClose}>Cancel</ActionButton></div>
    </form>
  );
}

/** The fallback rules: what may be conceded, per family; the co-pilot's quiet warning tests approved rules only. */
function Rules({ ctx }: { ctx: Ctx }) {
  const groups = rulesByFamily(ctx.book, ctx.view.family);
  if (groups.length === 0) return <div className="pbk-empty pbk-box"><p>No fallback rule yet{ctx.view.family && ctx.view.family !== 'yours' ? ` for ${ctx.view.family}` : ''}.</p></div>;
  return (
    <section className="pbk-box pbk-rules">
      {groups.map((g) => (
        <div key={g.family ?? 'all'} className="pbk-sec">
          <h3>{g.family ?? 'Every product'} <span>{g.rules.length} {g.rules.length === 1 ? 'rule' : 'rules'}, {g.rules.filter((x) => x.status === 'approved').length} approved</span></h3>
          <ul className="pbk-lines">{g.rules.map((rule) => <RuleItem key={rule.id} ctx={ctx} rule={rule} />)}</ul>
        </div>
      ))}
    </section>
  );
}

function RuleItem({ ctx, rule }: { ctx: Ctx; rule: BookEntry }) {
  const [editing, setEditing] = useState<string | null>(null);
  const act = (what: EntryAct) => ctx.press(rule.id, () => playbooksApi.act(ctx.store, rule.id, what, rule.version), (rows) => {
    const now = rows[0]!;
    return { text: what === 'approve' ? 'Approved. Used on calls from now.' : what === 'reject' ? 'Rejected.' : 'Retired.', undo: () => ctx.press(rule.id, () => playbooksApi.act(ctx.store, rule.id, 'restore', now.version), () => null, 'Not undone:') };
  }, what === 'approve' ? 'Not approved:' : 'Not changed:');
  const save = (e: FormEvent) => {
    e.preventDefault();
    if (!editing?.trim()) return;
    void ctx.press(rule.id, () => playbooksApi.words(ctx.store, rule.id, editing.trim(), rule.version), () => { setEditing(null); return { text: 'Approved as written. Used on calls from now.' }; }, 'Not approved:');
  };
  return (
    <li>
      {editing !== null ? (
        <form onSubmit={save}>
          <label className="pbk-sr" htmlFor={`r-${rule.id}`}>The rule</label>
          <ActionTextarea id={`r-${rule.id}`} className="pbk-area" value={editing} onChange={(e) => setEditing(e.target.value)} rows={3} />
          <div className="pbk-acts"><ActionButton type="submit" className="opt lead">Approve as written</ActionButton><ActionButton type="button" className="opt" onClick={() => setEditing(null)}>Cancel</ActionButton></div>
        </form>
      ) : <div className={rule.status === 'rejected' ? 'say struck' : 'say'}>{rule.body} <span className={rule.status === 'approved' ? 'ok' : 'st'}>{rule.status === 'open' ? 'still open: a number is in question' : stamp(rule)}</span></div>}
      {sourceWords(rule.source) ? <div className="pbk-src">{sourceWords(rule.source)}</div> : null}
      {ctx.notes[rule.id] ? <p className="pbk-err">{ctx.notes[rule.id]}</p> : null}
      {ctx.book.canApprove && editing === null ? (
        <div className="pbk-mini">
          {rule.status === 'draft' ? <ActionButton type="button" className="pbk-link" onClick={() => void act('approve')}>Approve</ActionButton> : null}
          <ActionButton type="button" className="pbk-link" onClick={() => setEditing(rule.status === 'open' ? rule.body : rule.body)}>{rule.status === 'open' ? 'Settle the number' : 'Edit'}</ActionButton>
          {rule.status !== 'rejected' ? <ActionButton type="button" className="pbk-link" onClick={() => void act('reject')}>Reject</ActionButton> : <ActionButton type="button" className="pbk-link" onClick={() => void act('restore')}>Bring it back</ActionButton>}
          {rule.status !== 'rejected' ? <ActionButton type="button" className="pbk-link" onClick={() => void act('retire')}>Retire</ActionButton> : null}
        </div>
      ) : null}
    </li>
  );
}

export type { BookObjection, ReactNode };
